// Used by: many pages
import { useCallback, useEffect, useContext } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import poweredByContext from "../../store/PoweredByContext";
import { replaceShyName } from "../../utilities/string";
import { getUrl } from "../../utilities/url";
import { getSchema, getPageSchema } from "../../utilities/poweredBy";
import reactManifest from "react-manifest";

const SEO = ({ title, description, type, image, date, canonicalUrl, isHomepage, noindex }) => {
    // Hooks
    const poweredByCtx = useContext(poweredByContext);

    const siteName = poweredByCtx.isPoweredBy ? poweredByCtx.tenant.tenantName : "Shy Aviation";
    const url = getUrl(poweredByCtx, isHomepage ? "/" : window.location.pathname);

    type = type || "website";

    /*
     * Replace Shy Aviation with the tenant name, if necessary
     */
    const replaceName = (str) => {
        if (str) {
            if (poweredByCtx.tenant) {
                return replaceShyName(str, poweredByCtx.isPoweredBy, poweredByCtx.tenant.tenantName);
            }

            return str;
        }
    };

    const getTitle = () => {
        if (title) {
            return replaceName(title);
        } else {
            const el = document.querySelector("title");

            if (el) {
                return replaceName(el.innerHTML);
            }
        }
    };

    const replaceDescription = useCallback(() => {
        const metaDisc = document.querySelector('meta[name="description"]');

        if (metaDisc) {
            if (description) {
                metaDisc.setAttribute("content", description);
            }
        }
    }, [description]);

    useEffect(() => {
        if (!poweredByCtx.siteReady) return;
        if (description) {
            replaceDescription();
        }
    }, [description, poweredByCtx, replaceDescription]);

    useEffect(() => {
        // For Powered By sites (e.g., JetSphere), do not update head until tenant data is ready
        if (!poweredByCtx.siteReady) return;
        const icon = document.querySelector('link[rel="icon"]');
        const touchIcon = document.querySelector('link[rel="apple-touch-icon"]');

        if (poweredByCtx.isPoweredBy && icon) {
            // Update favicon
            if (poweredByCtx?.tenant.favIcon) {
                icon.href = poweredByCtx.tenant.favIcon;
            } else {
                console.log(`favicon not available for ${poweredByCtx.tenantID}`);
            }
        }
        if (poweredByCtx.isPoweredBy && touchIcon) {
            // Update touch icon
            if (poweredByCtx?.tenant.appleTouchIcon) {
                touchIcon.href = poweredByCtx.tenant.appleTouchIcon;
            } else {
                console.log(`apple-touch-icon not available for ${poweredByCtx.tenantID}`);
            }
        }

        const upsertLdJson = (id, data) => {
            let el = document.getElementById(id);
            if (!data) {
                if (el) el.remove();
                return;
            }
            if (!el) {
                el = document.createElement("script");
                el.setAttribute("type", "application/ld+json");
                el.id = id;
                document.body.appendChild(el);
            }
            el.textContent = JSON.stringify(data);
        };

        // Remove any pre-existing unlabelled JSON-LD
        document
            .querySelectorAll('script[type="application/ld+json"]:not(#ld-organisation):not(#ld-page)')
            .forEach((el) => el.remove());

        upsertLdJson("ld-organisation", getSchema(poweredByCtx));
        upsertLdJson(
            "ld-page",
            getPageSchema({
                poweredByCtx,
                type,
                title: getTitle(),
                description,
                image,
                date,
                pathname: window.location.pathname,
                isHomepage,
            })
        );

        // Update manifest
        if (poweredByCtx.isPoweredBy) {
            const manifestConfig = {
                short_name: poweredByCtx?.tenant?.tenantName,
                name: poweredByCtx?.tenant?.tenantName,
                icons: {
                    48: `favicon-${poweredByCtx.tenantID}.ico`,
                    180: `apple-touch-icon-${poweredByCtx.tenantID}.png`,
                },
            };

            reactManifest.update(manifestConfig, "#manifest-placeholder");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poweredByCtx]);

    if (!poweredByCtx.siteReady) return null;

    return (
        <HelmetProvider>
            <Helmet>
                {/* Title */}
                <title>{getTitle()}</title>

                {/* Prevent indexing of error/non-canonical pages */}
                {noindex && <meta name="robots" content="noindex, follow" />}

                {/* Description (optional) */}
                {description && <meta itemprop="description" value={description} />}

                {/* Twitter */}
                <meta name="twitter:title" content={getTitle()} />
                <meta name="twitter:url" content={url} />
                <meta name="twitter:card" content="summary" />
                {description && <meta property="twitter:description" value={description} />}
                {image && <meta property="twitter:image" value={image} />}

                {/* Facebook */}
                <meta property="og:site_name" content={siteName} />
                <meta property="og:title" content={getTitle()} />
                <meta property="og:url" content={url} />
                <meta property="og:type" content={type} />
                {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
                {description && <meta property="og:description" value={description} />}
                {image && <meta property="og:image" value={image} />}

                {/* Generic */}
                <meta itemprop="name" content={getTitle()} />
                <meta itemprop="url" content={url} />
                {description && <meta itemprop="description" value={description} />}
                {image && <meta itemprop="image" value={image} />}
                {image && <meta itemprop="thumbnailUrl" value={image} />}
                {type === "article" && <meta itemprop="author" content={siteName} />}
                {date && <meta itemprop="datePublished" content={date} />}
            </Helmet>
        </HelmetProvider>
    );
};

export default SEO;
